export const DEFAULT_MAX_SEATS = 4
export const MAX_MARGIN_KM = 200

const MAX_CODE_LENGTH = 30
const CODE_PATTERN = /^[A-Z0-9_-]{3,30}$/
const COORDINATES_PATTERN = /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/
const LOCAL_OFFSET = '-03:00'
const LOCAL_OFFSET_MS = 3 * 60 * 60 * 1000
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

const plural = (amount, singular, pluralForm) => `${amount} ${amount === 1 ? singular : pluralForm}`

export const parseNumber = value => {
  const text = String(value ?? '').trim().replace(',', '.')
  return text === '' ? NaN : Number(text)
}

export const parseCoordinates = text => {
  const match = String(text || '').trim().match(COORDINATES_PATTERN)
  if (!match) return null
  const lat = Number(match[1])
  const lng = Number(match[2])
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}

export const formatCoordinates = ({ lat, lng }) => `${lat}, ${lng}`

export const mapsUrl = ({ lat, lng }) => `https://www.google.com/maps?q=${lat},${lng}`

export const localDateInput = timestamp =>
  new Date(timestamp - LOCAL_OFFSET_MS).toISOString().slice(0, 10)

export const expirationToDateInput = expiresAt => {
  if (!expiresAt) return ''
  const date = new Date(expiresAt)
  if (Number.isNaN(date.getTime())) return ''
  return localDateInput(date.getTime())
}

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const endOfLocalDay = value =>
  DATE_INPUT_PATTERN.test(value) ? new Date(`${value}T23:59:59${LOCAL_OFFSET}`) : null

export const isValidDateInput = value => {
  const date = endOfLocalDay(value)
  return !!date && !Number.isNaN(date.getTime())
}

export const dateInputToExpiration = value =>
  isValidDateInput(value) ? endOfLocalDay(value).toISOString() : null

export const isPastDateInput = (value, now = Date.now()) =>
  isValidDateInput(value) && endOfLocalDay(value).getTime() <= now

export const formatDay = dateInput => {
  const [year, month, day] = dateInput.split('-').map(Number)
  return `${day} ${MONTHS[month - 1]} ${year}`
}

export const expirationState = (expiresAt, now = Date.now()) => {
  const dateInput = expirationToDateInput(expiresAt)
  if (!dateInput) return null
  return { label: formatDay(dateInput), expired: new Date(expiresAt).getTime() <= now }
}

export const describeAmount = ({ percentage }) => `${percentage}%`

const locationLabel = location => location.name || formatCoordinates(location)

export const describeRoute = ({ locations = [] }) => {
  if (locations.length === 1) return `Desde o hacia ${locationLabel(locations[0])}`
  return locations.map(locationLabel).join(' ↔ ')
}

export const describeCaps = discount => {
  const uses = discount.max_uses_per_month
  const seats = discount.max_seats_per_month ?? DEFAULT_MAX_SEATS
  return {
    label: [
      uses != null && plural(uses, 'compra', 'compras'),
      `${plural(seats, 'asiento', 'asientos')} por persona`
    ]
      .filter(Boolean)
      .join(' · '),
    isDefault: discount.max_uses_per_month == null && discount.max_seats_per_month == null
  }
}

export const hasAdvancedConfig = discount =>
  !!(discount.target_pricing || discount.variants_by_lobby_type || discount.ab_test)

export const describeConditions = discount =>
  [
    discount.requires_first_trip && 'Sólo primer viaje',
    discount.requires_notified_lobby_view && 'Push de vista abandonada',
    discount.fake && 'Medición: no se cobra',
    discount.show_in_similars === false && 'Oculto en similares',
    discount.shares_limit_with?.length > 0 &&
      `Tope compartido con ${discount.shares_limit_with.join(', ')}`,
    discount.blacklist?.length > 0 && plural(discount.blacklist.length, 'bloqueado', 'bloqueados'),
    hasAdvancedConfig(discount) && 'Config. avanzada'
  ].filter(Boolean)

const EMPTY_LOCATION = { name: '', coordinates: '' }

export const EMPTY_FORM = {
  code: '',
  active: false,
  percentage: '',
  locations: [EMPTY_LOCATION, EMPTY_LOCATION],
  margin: '30',
  requires_first_trip: false,
  requires_notified_lobby_view: false,
  max_uses_per_month: '',
  max_seats_per_month: '',
  shares_limit_with: '',
  fake: false,
  show_in_similars: true,
  blacklist: '',
  expires_at: ''
}

export const formFromDiscount = discount =>
  discount
    ? {
        code: discount.code,
        active: !!discount.active,
        percentage: String(discount.percentage ?? ''),
        locations: (discount.locations || []).map(location => ({
          name: location.name || '',
          coordinates: formatCoordinates(location)
        })),
        margin: String(discount.margin ?? ''),
        requires_first_trip: !!discount.requires_first_trip,
        requires_notified_lobby_view: !!discount.requires_notified_lobby_view,
        max_uses_per_month: discount.max_uses_per_month == null ? '' : String(discount.max_uses_per_month),
        max_seats_per_month:
          discount.max_seats_per_month == null ? '' : String(discount.max_seats_per_month),
        shares_limit_with: (discount.shares_limit_with || []).join(', '),
        fake: !!discount.fake,
        show_in_similars: discount.show_in_similars !== false,
        blacklist: (discount.blacklist || []).join('\n'),
        expires_at: expirationToDateInput(discount.expires_at)
      }
    : EMPTY_FORM

export const formForDuplicate = (discount, now = Date.now()) => {
  const form = formFromDiscount(discount)
  return {
    ...form,
    code: '',
    shares_limit_with: [...new Set([discount.code, ...(discount.shares_limit_with || [])])].join(', '),
    expires_at: isPastDateInput(form.expires_at, now) ? '' : form.expires_at
  }
}

const codeWord = text => normalizeSearch(text).toUpperCase().replace(/[^A-Z0-9]/g, '')

const amountTag = ({ percentage }) => {
  const value = parseNumber(percentage)
  return Number.isNaN(value) ? '' : String(value).replace('.', '-')
}

const dayTag = now => {
  const [, month, day] = localDateInput(now).split('-')
  return `${day}${MONTHS[Number(month) - 1].toUpperCase()}`
}

const fitWords = (words, budget) => {
  const lengths = words.map(() => 0)
  let left = budget
  let growing = true
  while (left > 0 && growing) {
    growing = false
    for (let index = 0; index < words.length && left > 0; index += 1) {
      if (lengths[index] < words[index].length) {
        lengths[index] += 1
        left -= 1
        growing = true
      }
    }
  }
  return words.map((word, index) => word.slice(0, lengths[index]))
}

export const discountCode = (form, { takenCodes = [], now = Date.now() } = {}) => {
  const places = form.locations.map(location => codeWord(location.name)).filter(Boolean)
  if (places.length === 0) return ''
  const taken = new Set(takenCodes)
  for (let copy = 1; ; copy += 1) {
    const tail = [amountTag(form), dayTag(now), copy > 1 && copy].filter(Boolean).join('_')
    const budget = MAX_CODE_LENGTH - tail.length - 1 - Math.max(places.length - 1, 0)
    const route = fitWords(places, budget).join('-')
    const code = [route, tail].filter(Boolean).join('_')
    if (!taken.has(code)) return code
  }
}

const splitList = (text, separator, transform = item => item) =>
  [
    ...new Set(
      String(text || '')
        .split(separator)
        .map(item => transform(item.trim()))
        .filter(Boolean)
    )
  ]

const parseCap = value => (String(value).trim() === '' ? null : parseNumber(value))

const isValidCap = value => value === null || (Number.isInteger(value) && value > 0)

export const formErrors = (form, { creating, now = Date.now() }) => {
  const errors = {}
  const percentage = parseNumber(form.percentage)
  const margin = parseNumber(form.margin)

  if (creating && !CODE_PATTERN.test(form.code.trim().toUpperCase())) {
    errors.code = 'Completá la ruta y el porcentaje para armarlo'
  }

  if (creating && (Number.isNaN(percentage) || percentage <= 0 || percentage > 100)) {
    errors.percentage = 'Mayor a 0 y hasta 100'
  }

  if (form.locations.length === 0) errors.locations = 'Agregá al menos un punto'
  form.locations.forEach((location, index) => {
    if (creating && !codeWord(location.name)) {
      errors[`locations.${index}.name`] = 'Va en el código'
    }
    if (!parseCoordinates(location.coordinates)) {
      errors[`locations.${index}`] = 'Pegá latitud y longitud, por ejemplo -34.6037, -58.3816'
    }
  })

  if (Number.isNaN(margin) || margin <= 0 || margin > MAX_MARGIN_KM) {
    errors.margin = `Mayor a 0 y hasta ${MAX_MARGIN_KM} km`
  }

  if (form.expires_at && !isValidDateInput(form.expires_at)) {
    errors.expires_at = 'Fecha inválida'
  } else if (isPastDateInput(form.expires_at, now)) {
    errors.expires_at = 'Esa fecha ya pasó'
  }

  if (!isValidCap(parseCap(form.max_uses_per_month))) errors.max_uses_per_month = 'Entero mayor a 0'
  if (!isValidCap(parseCap(form.max_seats_per_month))) {
    errors.max_seats_per_month = 'Entero mayor a 0'
  }

  return errors
}

export const payloadFromForm = (form, { creating }) => ({
  ...(creating && {
    code: form.code.trim().toUpperCase(),
    percentage: parseNumber(form.percentage)
  }),
  active: form.active,
  locations: form.locations.map(location => ({
    ...(location.name.trim() && { name: location.name.trim() }),
    ...parseCoordinates(location.coordinates)
  })),
  margin: parseNumber(form.margin),
  requires_first_trip: form.requires_first_trip,
  requires_notified_lobby_view: form.requires_notified_lobby_view,
  max_uses_per_month: parseCap(form.max_uses_per_month),
  max_seats_per_month: parseCap(form.max_seats_per_month),
  shares_limit_with: splitList(form.shares_limit_with, /[,\s]+/, code => code.toUpperCase()),
  fake: form.fake,
  show_in_similars: form.show_in_similars,
  blacklist: splitList(form.blacklist, /[\n,]+/),
  expires_at: dateInputToExpiration(form.expires_at)
})

export const swapItems = (list, first, second) => {
  const from = list.indexOf(first)
  const to = list.indexOf(second)
  if (from < 0 || to < 0 || from === to) return list
  const next = [...list]
  ;[next[from], next[to]] = [next[to], next[from]]
  return next
}

export const capPartners = (discounts, saved) =>
  discounts.filter(
    discount =>
      discount._id !== saved._id &&
      (saved.shares_limit_with || []).includes(discount.code) &&
      !(discount.shares_limit_with || []).includes(saved.code)
  )

const normalizeSearch = text =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

export const filterDiscounts = (discounts, { query, status }) => {
  const term = normalizeSearch(query)
  return discounts.filter(discount => {
    if (status === 'active' && !discount.active) return false
    if (status === 'inactive' && discount.active) return false
    if (!term) return true
    return normalizeSearch(`${discount.code} ${describeRoute(discount)}`).includes(term)
  })
}
